/* Page grammar: the container, the header and the section card.
 *
 * WHY THIS EXISTS
 * ---------------
 * The 2026-09 audit found four different page-title sizes, three
 * different container widths and eleven variants of "grey uppercase
 * micro-label" across the builder alone, plus a console that renders
 * section headers as solid violet bands. None of that is a decision
 * anybody made; it is what happens when every page hand-rolls its own
 * chrome. These three components are the chrome, once.
 *
 * NO i18n IN THIS PACKAGE. Every string arrives as a prop. The builder
 * drives react-i18next and the console drives its own ``useT``; a
 * package that reached for either would have to be initialised twice and
 * would render raw keys the first time somebody forgot. The caller owns
 * the copy.
 *
 * COLOURS are the Tailwind neutral scale on purpose, not the semantic
 * tokens of design-language.md §9. The builder's dark theme currently
 * works by rewriting exactly those utilities with ``!important``, so
 * using them keeps dark mode behaving as it does today. The token
 * migration lands as its own change, in one place, once every screen is
 * on these components.
 */

import { Button } from "@omnitok/ui";
import { HelpCircle } from "lucide-react";
import type { ReactNode } from "react";

/** Joins class names, skipping the falsy ones. */
export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

export interface PageContainerProps {
  children: ReactNode;
  /** Full-bleed layout. The report editor is the only legitimate user:
   *  it is a canvas plus an inspector, not a document. */
  wide?: boolean;
  className?: string;
}

/**
 * The one page width: 1152 px, centred. Every admin screen sits in one
 * of these so navigating between sections does not resize the content.
 */
export function PageContainer({
  children,
  wide = false,
  className,
}: PageContainerProps): ReactNode {
  return (
    <div
      className={cx(
        "mx-auto w-full px-4 py-6 sm:px-8",
        wide ? "max-w-none" : "max-w-6xl",
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface PageHeaderProps {
  title: string;
  /** One sentence. Anything longer belongs behind ``onHelp``. */
  subtitle?: string;
  /** Rendered to the left of the title — a status chip, a back link. */
  eyebrow?: ReactNode;
  /** At most one primary and one secondary control. A third goes into an
   *  overflow menu; the header is not a toolbar. */
  actions?: ReactNode;
  /** Opens the contextual help rail. Promoted from the console, where it
   *  is the reason its long prose does not have to live in the form. */
  onHelp?: () => void;
  /** Label for the help button — the caller translates it. */
  helpLabel?: string;
}

export function PageHeader({
  title,
  subtitle,
  eyebrow,
  actions,
  onHelp,
  helpLabel = "?",
}: PageHeaderProps): ReactNode {
  return (
    <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        {eyebrow !== undefined && <div className="mb-1">{eyebrow}</div>}
        {/* Look v3 (2026-10-24): the same head as a report (ReportHeader). */}
        <h1 className="font-display text-[25px] font-extrabold leading-[1.25] tracking-[-0.5px] text-neutral-800">{title}</h1>
        {subtitle !== undefined && subtitle !== "" && (
          <p className="mt-[3px] max-w-2xl text-xs leading-[1.6] text-neutral-500">
            {subtitle}
          </p>
        )}
      </div>
      {(actions !== undefined || onHelp !== undefined) && (
        // Wraps instead of overflowing: on a narrow screen the actions
        // drop under the title and then onto as many lines as they need.
        <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
          {onHelp !== undefined && (
            <Button
              variant="outline"
              size="sm"
              leftIcon={<HelpCircle size={16} />}
              onClick={onHelp}
            >
              {helpLabel}
            </Button>
          )}
          {actions}
        </div>
      )}
    </header>
  );
}

export interface SectionProps {
  title?: string;
  description?: string;
  /** Right-hand action for this section only. */
  actions?: ReactNode;
  children: ReactNode;
  /** Drop the body padding — for a section whose body is a DataTable. */
  flush?: boolean;
  className?: string;
}

/**
 * A titled white card. Two levels of these is the maximum; the audit
 * found three in Integraciones and in the console's Jobs page, where a
 * card wraps a violet band that wraps a table with a violet header.
 */
export function Section({
  title,
  description,
  actions,
  children,
  flush = false,
  className,
}: SectionProps): ReactNode {
  const hasHeader =
    title !== undefined || description !== undefined || actions !== undefined;
  return (
    <section
      className={cx(
        "rounded-[14px] border border-neutral-200 bg-white shadow-card",
        className,
      )}
    >
      {hasHeader && (
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-neutral-200 px-5 py-4">
          <div className="min-w-0">
            {title !== undefined && (
              <h2 className="font-display text-[15px] font-extrabold text-neutral-900">
                {title}
              </h2>
            )}
            {description !== undefined && (
              <p className="mt-0.5 max-w-3xl text-xs leading-relaxed text-neutral-500">
                {description}
              </p>
            )}
          </div>
          {actions !== undefined && (
            <div className="flex flex-shrink-0 items-center gap-2">
              {actions}
            </div>
          )}
        </div>
      )}
      <div className={flush ? "" : "px-5 py-4"}>{children}</div>
    </section>
  );
}

/** Vertical rhythm between sections, so pages stop inventing margins. */
export function SectionStack({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): ReactNode {
  return <div className={cx("space-y-5", className)}>{children}</div>;
}
